import { useCallback, useState, useMemo } from 'react';
import ListPickerAdd from './ListPickerAdd';
import { ScrollArea } from '@/src/shadComponents/ui/scroll-area';
import { Checkbox } from '@/src/shadComponents/ui/checkbox';
import { Label } from '@/src/shadComponents/ui/label';
import { Button } from '@/src/shadComponents/ui/button';

import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/src/shadComponents/ui/select';
import { Search } from 'lucide-react';
import { cn } from '@/src/lib/utils';

interface ListItem {
    id: number;
    name: string;
}

interface Organization {
    id: number;
    name: string;
}

interface EnhancedListPickerModalProps {
    title: string;
    list: ListItem[];
    selectedIds: number[];
    onSave: (
        selectedIds: number[],
        crossOrgSelections?: Record<number, number[]>,
    ) => void;
    onClose: () => void;
    allowAdd?: boolean;
    onAdd?: (label: string) => void;
    // New props for organization selection
    showOrgSelector?: boolean;
    organizations?: Organization[];
    selectedOrgId?: number;
    onOrgChange?: (orgId: number) => void;
    // Props for cross-org selection persistence
    crossOrgSelections?: Record<number, number[]>;
}

export default function EnhancedListPickerModal({
    title,
    list,
    selectedIds,
    onClose,
    onSave,
    allowAdd = false,
    onAdd,
    showOrgSelector = false,
    organizations = [],
    selectedOrgId,
    onOrgChange,
    crossOrgSelections = {},
}: EnhancedListPickerModalProps) {
    const [localSelectedIds, setLocalSelectedIds] = useState(selectedIds);
    const [searchTerm, setSearchTerm] = useState('');
    const [localCrossOrgSelections, setLocalCrossOrgSelections] =
        useState<Record<number, number[]>>(crossOrgSelections);

    const filteredList = useMemo(() => {
        if (!searchTerm.trim()) {
            return list;
        }
        return list.filter((item) =>
            item.name.toLowerCase().includes(searchTerm.toLowerCase()),
        );
    }, [list, searchTerm]);

    const onChange = useCallback(
        (listItemId: number) => {
            if (!localSelectedIds.includes(listItemId)) {
                setLocalSelectedIds([...localSelectedIds, listItemId]);
            } else {
                const modifiedIds = [...localSelectedIds];
                modifiedIds.splice(modifiedIds.indexOf(listItemId), 1);
                setLocalSelectedIds(modifiedIds);
            }
        },
        [localSelectedIds],
    );

    const handleOrgChange = useCallback(
        (orgIdString: string) => {
            const newOrgId = parseInt(orgIdString);

            // Save current selections for the current org
            if (selectedOrgId && localSelectedIds.length > 0) {
                setLocalCrossOrgSelections((prev) => ({
                    ...prev,
                    [selectedOrgId]: localSelectedIds,
                }));
            }

            // Restore selections for the new org or start with empty array
            const newOrgSelections = localCrossOrgSelections[newOrgId] || [];
            setLocalSelectedIds(newOrgSelections);
            setSearchTerm('');

            if (onOrgChange) {
                onOrgChange(newOrgId);
            }
        },
        [onOrgChange, selectedOrgId, localSelectedIds, localCrossOrgSelections],
    );

    return (
        <div
            className="fixed inset-0 bg-black/50 bg-opacity-80 flex justify-center items-center z-50"
            onClick={(e) => {
                e.stopPropagation();
                onClose();
            }}
        >
            <div
                className="bg-background shadow-sm sm:rounded-lg w-[90%] lg:w-[80%] relative pb-[40px]"
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <div className="px-4 py-5 sm:p-6">
                    <div className="flex justify-between items-start lg:items-center">
                        <div className="flex flex-row lg:flex-row gap-2 w-full">
                            <h2 className="text-lg font-semibold tracking-tight text-foreground">
                                {title}
                            </h2>
                            <div className="flex max-w-xl gap-4 justify-end">
                                <div
                                    className={cn(
                                        'flex flex-row search-input-wrapper search-flex-row gap-[30px] transition-all duration-300',
                                        '',
                                    )}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                    }}
                                >
                                    {showOrgSelector && (
                                        <div className="mb-4">
                                            <Select
                                                value={selectedOrgId?.toString()}
                                                onValueChange={handleOrgChange}
                                            >
                                                <SelectTrigger className="w-full">
                                                    <SelectValue placeholder="Select organization" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {organizations.map(
                                                        (org) => (
                                                            <SelectItem
                                                                key={org.id}
                                                                value={org.id.toString()}
                                                            >
                                                                {org.name}
                                                            </SelectItem>
                                                        ),
                                                    )}
                                                </SelectContent>
                                            </Select>
                                        </div>
                                    )}
                                    <div className="mb-4 min-w-[200px] mt-2">
                                        <div className="flex items-center gap-2 rounded-lg border border-input px-3 focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50">
                                            <Search className="size-4 shrink-0 text-muted-foreground" />
                                            <input
                                                type="text"
                                                placeholder="Search..."
                                                value={searchTerm}
                                                onChange={(e) =>
                                                    setSearchTerm(
                                                        e.target.value,
                                                    )
                                                }
                                                className="h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        {/* Organization Selector */}
                    </div>
                    {/* Search Input */}

                    {allowAdd && <ListPickerAdd onSubmit={onAdd} />}

                    <ScrollArea className="h-[400px] rounded-md border p-4">
                        <div
                            className="list-2"
                            onClick={(e) => e.stopPropagation()}
                        >
                            {filteredList.length === 0 ? (
                                <div className="text-center text-muted-foreground py-4">
                                    {searchTerm
                                        ? 'No items match your search'
                                        : 'No items available'}
                                </div>
                            ) : (
                                filteredList.map((listItem) => (
                                    <div
                                        key={listItem.id}
                                        className="list-item"
                                    >
                                        <Checkbox
                                            onClick={() =>
                                                onChange(listItem.id)
                                            }
                                            id={`${listItem.id}_item`}
                                            checked={localSelectedIds.includes(
                                                listItem.id,
                                            )}
                                        />
                                        <Label
                                            className=" pl-4"
                                            htmlFor={`${listItem.id}_item`}
                                        >
                                            {listItem.name}
                                        </Label>
                                    </div>
                                ))
                            )}
                        </div>
                    </ScrollArea>

                    <br />
                    <div className="flex flex-row justify-between">
                        <div className="text-sm text-muted-foreground">
                            {localSelectedIds.length} selected
                            {filteredList.length !== list.length && (
                                <span className="ml-2">
                                    ({filteredList.length} of {list.length}{' '}
                                    shown)
                                </span>
                            )}
                        </div>
                        <div className="flex flex-row gap-2">
                            <Button
                                className="Tertiary-button"
                                onClick={() => {
                                    // Save current selections for the current org before final save
                                    let finalCrossOrgSelections = {
                                        ...localCrossOrgSelections,
                                    };
                                    if (
                                        selectedOrgId &&
                                        localSelectedIds.length > 0
                                    ) {
                                        finalCrossOrgSelections[selectedOrgId] =
                                            localSelectedIds;
                                    }

                                    // If we're using cross-org selection, pass the full selections map
                                    if (
                                        showOrgSelector &&
                                        Object.keys(finalCrossOrgSelections)
                                            .length > 0
                                    ) {
                                        onSave(
                                            localSelectedIds,
                                            finalCrossOrgSelections,
                                        );
                                    } else {
                                        onSave(localSelectedIds);
                                    }
                                }}
                            >
                                Save
                            </Button>
                            <Button
                                className="Secondary-button"
                                onClick={onClose}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
